import { apiUrl } from "../../api/endpoints";
import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import DataGridTable from "../../Components/DataGridTable";
import Spinner from "../../Components/Spinner";

import { useAuth } from "../../hooks/AuthProvider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import Alert from "../../Components/Alert";

import { LanguageContext } from "../../context/LanguageContext";
import "../../Components/modal.css";
import "./UserHealthDetails.css";
import {
  getHealthTranslations,
  HealthPageHeader,
  PermissionDenied,
  isRtlLanguage,
} from "./userHealthShared";

export const MedicalDevices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deviceFrom, setDeviceFrom] = useState("");
  const [selectedSubid, setSelectedSubid] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const { permissions, user } = useAuth();

  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);

  const canView = permissions["view-medical-devices"];
  const isSuper = String(user?.role || "").toLowerCase() === "super";

  const isRtl = isRtlLanguage(lang);
  const canFinishScan = permissions["health_device_finish_scan"];
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const pagingoptions = [10, 20, 50, 100];
  const getScanStatusLabel = (row) => {
    const raw = String(row.scanStatus || "").toLowerCase();
    if (raw === "scanning") return t.labels.scanning;
    if (raw === "avaliable" || raw === "available") return t.labels.available;
    return row.scanStatus || t.labels.unknown;
  };

  const getScanStatusClass = (row) =>
    String(row.scanStatus || "").toLowerCase() === "scanning" ? "scanning" : "available";

  const showAlert = (type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? "left" : "right",
    });
  };

  const fetchData = useCallback(async () => {
    if (!canView) return;

    setLoading(true);

    try {
      const sort = sortModel?.[0] || {};
      const res = await axios.get(apiUrl(`medical-device/list`), {
        params: {
          ...(isSuper && selectedSubid ? { subid: selectedSubid } : {}),
          page,
          pageSize,
          sortField: sort.field || "id",
          sortOrder: sort.sort || "desc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });
      const payload = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.DATA)
          ? res.data.DATA
          : [];

      if (Array.isArray(payload)) {
        const formatted = payload.map((item) => ({
          ...item,
          userName: item.user?.name || item.userName || "-",
          subscriberName: item.subscriber?.name || item.subscriberName || "-",
        }));

        setRows(formatted);
        setRowCount(Number(res.data?.pagination?.total ?? formatted.length));
      } else {
        setRows([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("MedicalDevices fetch error:", err);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, isSuper, page, pageSize, selectedSubid, sortModel]);

  const fetchSubscribers = useCallback(async () => {
    if (!canView || !isSuper) return;

    try {
      const res = await axios.get(apiUrl("medical-device/control-data"));
      const data = res.data?.DATA || {};
      setSubscribers(Array.isArray(data.subscribers) ? data.subscribers : []);
    } catch (err) {
      console.error("MedicalDevices subscribers fetch error:", err);
      setSubscribers([]);
    }
  }, [canView, isSuper]);

  const markAvailable = useCallback(async (row) => {
    try {
      setLoading(true);
      await axios.post(apiUrl(`medical-device/finish-scan`), {
        healthDeviceId: row.id,
      });
      await fetchData();
    } catch (err) {
      console.error("MedicalDevices finish scan error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  const createDevice = async () => {
    const normalizedDeviceFrom = deviceFrom.trim();

    if (!normalizedDeviceFrom) {
      showAlert("error", t.states.deviceFromRequired || "deviceFrom is required");
      return;
    }

    try {
      setCreating(true);
      await axios.post(apiUrl("medical-device/create"), {
        deviceFrom: normalizedDeviceFrom,
        ...(isSuper && selectedSubid ? { subid: selectedSubid } : {}),
      });
      setDeviceFrom("");
      setOpenCreate(false);
      showAlert("success", t.states.deviceCreated || "Medical device created");
      await fetchData();
    } catch (err) {
      const message =
        err?.response?.data?.INFO ||
        err?.response?.data?.message ||
        err.message ||
        (t.states.deviceCreateFailed || "Failed to create medical device");

      showAlert("error", message);
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    ...(canFinishScan ? [{
      field: "actions",
      headerName: "",
      type: "actions",
      width: 80,
      getActions: (params) => {
        const isScanning = String(params.row.scanStatus || "").toLowerCase() === "scanning";
        if (!isScanning) return [];

        return [
          <GridActionsCellItem
            key="mark-available"
            icon={
              <Tooltip title={t.actions.markAvailable}>
                <CheckCircleOutlineIcon
                  style={{
                    color: "var(--health-success-color)",
                    fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
                  }}
                />
              </Tooltip>
            }
            label={t.actions.markAvailable}
            showInMenu
            onClick={() => markAvailable(params.row)}
          />,
        ];
      },
    }] : []),
    {
      field: "deviceFrom",
      headerName: t.columns.deviceFrom || "Device From",
      width: 220,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => params.row.deviceFrom || "-",
    },
    ...(isSuper ? [{
      field: "subscriberName",
      headerName: t.columns.subscriber,
      width: 220,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => params.row.subscriberName || "-",
    }] : []),
    {
      field: "userName",
      headerName: t.columns.user,
      width: 250,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "scanStatus",
      headerName: t.columns.scanStatus,
      width: 150,
      align: "center",
      headerAlign: "center"
,
      renderCell: (params) => {
        const statusClass = getScanStatusClass(params.row);
        const label = getScanStatusLabel(params.row);
        const verified = params.row.externalStatusChecked && params.row.externalScanStatus;

        return (
          <span className="health-status-cell">
            <span>
              <span className={`health-status-dot ${statusClass}`} />
              {label}
            </span>
            {verified ? (
              <small>{t.labels.anovatorVerified || "Anovator verified"}</small>
            ) : params.row.externalStatusError ? (
              <small className="health-status-cell__warning">
                {t.labels.localFallback || "Local fallback"}
              </small>
            ) : null}
          </span>
        );
      }
    },
  
  ];
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  if (!canView) {
    return (
      <PermissionDenied title={t.states.permissionDenied} message={t.states.cannotViewHealth} />
    );
  }

  return (
    <div className="configuration devices-page health-page" dir={isRtl ? "rtl" : "ltr"} style={{ height: "100%" }}>
      <div className="device-main health-page-shell">
        <HealthPageHeader
          title={t.pages.medicalDevices}
          subtitle={t.descriptions.medicalDevices}
          actions={
            <div className="health-device-header-controls">
              {isSuper ? (
                <label className="health-header-filter">
                  <span>{t.columns.subscriber}</span>
                  <select
                    value={selectedSubid}
                    onChange={(event) => {
                      setSelectedSubid(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">{t.actions.allSubscribers || "All subscribers"}</option>
                    {subscribers.map((subscriber) => (
                      <option key={subscriber.id} value={subscriber.id}>
                        {subscriber.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <button
                className="devices-secondary-btn"
                onClick={fetchData}
                disabled={loading}
                title={t.actions.refreshStatus || "Refresh status"}
              >
                <RefreshIcon style={{ marginInlineEnd: "8px" }} />
                <span>{t.actions.refreshStatus || "Refresh Status"}</span>
              </button>
              <button
                className="devices-primary-btn"
                onClick={() => {
                  setDeviceFrom("");
                  setOpenCreate(true);
                }}
              >
                <AddIcon style={{ marginInlineEnd: "8px" }} />
                <span>{t.actions.addDevice || "Add Device"}</span>
              </button>
            </div>
          }
        />

        {openCreate ? (
          <div id="modal" className="health-scan-modal-overlay" onMouseDown={() => setOpenCreate(false)}>
            <div
              id="popup"
              className="dashboard-modal health-scan-modal"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="dashboard-header health-scan-modal__header">
                <div className="dashboard-header-text">
                  <h2>{t.actions.addDevice || "Add Device"}</h2>
                </div>
              </div>
              <div className="modal-divider" />
              <div className="modal-content dashboard-content health-scan-modal__content">
                <label className="health-device-from-field">
                  <span>
                    <strong>{t.columns.deviceFrom || "Device From"}</strong>
                    <small>{t.actions.deviceFromHint || "Enter the exact deviceFrom value sent by the webhook."}</small>
                  </span>
                </label>
                <input
                  className="health-device-from-input"
                  value={deviceFrom}
                  onChange={(event) => setDeviceFrom(event.target.value)}
                  placeholder="A5@9CB8B42A2F7B"
                  autoFocus
                />
              </div>
              <div className="modal-divider" />
              <div className="modal-footer dashboard-footer health-scan-modal__footer">
                <div className="modal-footer-right-buttons">
                  <button
                    type="button"
                    className="btn btn-cancel-details"
                    onClick={() => setOpenCreate(false)}
                  >
                    {t.actions.cancel}
                  </button>
                  <button
                    type="button"
                    className="btn btn-apply"
                    onClick={createDevice}
                    disabled={creating}
                  >
                    {creating ? (t.actions.creating || "Creating...") : (t.actions.create || "Create")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="table-wrapper" style={{ flex: 1 }}>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              showToolbar={true}
              tableKey="medical_devices"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: setPage,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}

            />
          </div>
        ) : (
          <div className="health-empty-state">{t.states.noData}</div>
        )}
      </div>
    </div>
  );
};
